import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "./ui/Button";
import { isChunkLoadError, reloadOnceForStaleChunk } from "@/lib/staleChunk";

interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
  /** A stale tab after a deploy: the page is reloading itself. */
  reloading: boolean;
}

/** Catches render errors that would otherwise blank the page, so a bug in one
 * feature shows a recoverable message instead of a white screen. */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, reloading: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // A lazy page whose chunk a deploy removed: reload onto the new build.
    if (isChunkLoadError(error) && reloadOnceForStaleChunk()) {
      this.setState({ reloading: true });
      return;
    }
    console.error("Unhandled render error:", error, info.componentStack);
  }

  render() {
    if (this.state.reloading) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 text-center text-sm text-slate-500">
          Loading the latest version...
        </div>
      );
    }
    if (this.state.error) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-50 px-4 text-center">
          <div className="rounded-full bg-red-100 p-3 text-red-600">
            <svg className="h-8 w-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
              />
            </svg>
          </div>
          <div>
            <h1 className="text-lg font-semibold text-slate-900">Something went wrong</h1>
            <p className="mt-1 max-w-sm text-sm text-slate-500">
              The page ran into an unexpected error. Reloading usually fixes it.
            </p>
          </div>
          <Button onClick={() => window.location.reload()}>Reload page</Button>
        </div>
      );
    }
    return this.props.children;
  }
}
